/**
 * Build a human-readable summary of schedule vs. forecast/actual balance.
 *
 * • Drops the “grade” argument.
 * • Adds an overview line tailored to which issues (if any) are detected.
 * • Shows the worst-period message only when an over-budget or understaffed
 *   condition exists.
 */
export function generateGradeMessage(
  scheduledLabor,
  forecastedLabor,
  getTimeString
) {
  /* ------------------------------------------------------------------ *\
     0 ░ Bail out early if there is no usable data
  \* ------------------------------------------------------------------ */
  const totalSched = scheduledLabor.reduce((s, r) => s + r.hours, 0);
  const totalFcst = forecastedLabor.reduce((s, r) => s + r.hours, 0);
  const hrsDiff = totalSched - totalFcst; // + ⇒ over budget

  if (totalSched === 0 || totalFcst === 0) {
    // Nothing to compare ⇒ don’t show any tooltip
    return "";
  }

  /* ------------------------------------------------------------------ *\
     1 ░ Build lookup tables  (qh_num → totals)
  \* ------------------------------------------------------------------ */
  const schedMap = {};
  const schedLaborMap = {};
  scheduledLabor.forEach((r) => {
    schedMap[r.qh_num] = (schedMap[r.qh_num] || 0) + r.hours;
    schedLaborMap[r.qh_num] = (schedLaborMap[r.qh_num] || 0) + r.labor;
  });

  const forecastMap = {};
  const forecastLaborMap = {};
  forecastedLabor.forEach((r) => {
    forecastMap[r.qh_num] = (forecastMap[r.qh_num] || 0) + r.hours;
    forecastLaborMap[r.qh_num] = (forecastLaborMap[r.qh_num] || 0) + r.labor;
  });

  /* ------------------------------------------------------------------ *\
     2 ░ Build timeline & diff segments
  \* ------------------------------------------------------------------ */
  const qhNums = Array.from(
    new Set([...Object.keys(schedMap), ...Object.keys(forecastMap)])
  )
    .map(Number)
    .sort((a, b) => a - b);

  const segments = [];
  let current = null;
  const flush = () => {
    if (current) {
      segments.push({ ...current });
      current = null;
    }
  };

  qhNums.forEach((qh) => {
    const diff = (schedMap[qh] || 0) - (forecastMap[qh] || 0);
    const type = diff > 0 ? "over" : diff < 0 ? "under" : null;

    if (!type) {
      flush();
      return;
    }

    if (current && current.type === type && qh <= current.endQh) {
      current.endQh = qh + 0.25;
      current.total += diff;
    } else {
      flush();
      current = { type, startQh: qh, endQh: qh + 0.25, total: diff };
    }
  });
  flush();

  // Sort by magnitude (largest first), tie-break by start time
  segments.sort((a, b) => {
    const mag = Math.abs(b.total) - Math.abs(a.total);
    return mag === 0 ? a.startQh - b.startQh : mag;
  });

  const fmtTime = (qh) => (getTimeString ? getTimeString(qh) : qh.toString());

  /* ------------------------------------------------------------------ *\
     3 ░ High-level numbers
  \* ------------------------------------------------------------------ */

  // Under-staffed quarter-hours (schedule labor +1 < forecast labor)
  const understaffedQH = qhNums.reduce(
    (cnt, qh) =>
      cnt +
      ((schedLaborMap[qh] || 0) + 1 < (forecastLaborMap[qh] || 0) ? 1 : 0),
    0
  );
  const totalForecastQH = Object.values(forecastLaborMap).filter(
    (value) => value > 0
  ).length;
  const understaffedPct =
    totalForecastQH > 0 ? (understaffedQH / totalForecastQH) * 100 : 0;

  /* ------------------------------------------------------------------ *\
     4 ░ Overview + summary bullets
  \* ------------------------------------------------------------------ */
  const overBudgetShown = hrsDiff > 2; // “over” by >2 hrs
  const understaffedShown = understaffedPct > 10;

  const lines = [];

  // ‣ Overview line
  if (!overBudgetShown && !understaffedShown) {
    lines.push(
      "Great balance of managing labor budget while ensuring proper coverage throughout the day."
    );
  } else if (overBudgetShown && !understaffedShown) {
    lines.push("Opportunity to save on labor costs.");
  } else if (!overBudgetShown && understaffedShown) {
    lines.push("Opportunity to improve coverage.");
  } else {
    lines.push("Opportunity to save on labor costs and to improve coverage.");
  }

  // ‣ Detailed bullets (only those that apply)
  if (overBudgetShown) {
    lines.push(`• ${hrsDiff.toFixed(2)} hours over daily budget`);
  }
  if (understaffedShown) {
    lines.push(`• ${understaffedPct.toFixed(0)}% of the day understaffed`);
  }

  /* ------------------------------------------------------------------ *\
     5 ░ Worst-period message (only if an issue exists)
  \* ------------------------------------------------------------------ */
  if (
    ((overBudgetShown && !understaffedShown) ||
      (understaffedShown && !overBudgetShown)) &&
    segments.length
  ) {
    const worst = segments[0];
    const hours = Math.abs(worst.total).toFixed(2);
    const start = fmtTime(worst.startQh);
    const end = fmtTime(worst.endQh);
    const label = worst.type === "over" ? "Overstaffed" : "Understaffed";
    lines.push(`• ${label} by ${hours} hrs at ${start}–${end}`);
  }

  /* ------------------------------------------------------------------ *\
     6 ░ Return final tooltip text
  \* ------------------------------------------------------------------ */
  return lines.join("\n");
}
